/**
 * HTTP client for the Messaging Service API.
 * Separate from the main CRM API.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { Result } from "../../../models/Result";
import { storageService } from "../../StorageService";
import { authService } from "../../authService";
import EventEmitter, { Events } from "../../eventemitter";

class MessagingHttpService {
    private instance: AxiosInstance;
    private baseUrl = process.env.REACT_APP_API_BASE_URL;

    constructor() {
        this.instance = axios.create({
            baseURL: this.baseUrl,
            timeout: 30000,
        } as AxiosRequestConfig);

        // Grab the token from storage and put it into the http
        const accessToken = storageService.getAccessToken();
        if (accessToken.length > 0) {
            this.setAuthToken(accessToken);
        }

        let isLoggingOut = false;

        this.instance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const status = error?.response?.status;

                if (status === 401) {
                    if (isLoggingOut) {
                        return Promise.reject(error);
                    }

                    isLoggingOut = true;

                    try {
                        const user = await authService.getUser();

                        if (user && !user.expired) {
                            EventEmitter.emit(Events.onLogout, "401");
                            return Promise.reject(error);
                        }

                        await authService.signinSilent();
                        isLoggingOut = false;
                        return this.instance.request(error.config);
                    } catch (refreshError) {
                        EventEmitter.emit(Events.onLogout, "401");
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            },
        );
    }

    setAuthToken = (token: string) => {
        this.instance.defaults.headers.common["Authorization"] = token;
    };

    get = async <T>(path: string, config?: AxiosRequestConfig): Promise<Result<T>> => {
        return this.send(() => this.instance.get<T>(path, config));
    };

    post = async <T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<Result<T>> => {
        return this.send(() => this.instance.post<T>(path, data, config));
    };

    put = async <T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<Result<T>> => {
        return this.send(() => this.instance.put<T>(path, data, config));
    };

    delete = async <T>(path: string, config?: AxiosRequestConfig): Promise<Result<T>> => {
        return this.send(() => this.instance.delete<T>(path, config));
    };

    private send = async <T>(promise: () => Promise<AxiosResponse<T>>): Promise<Result<T>> => {
        try {
            const response = await promise();

            return {
                success: response.status >= 200 && response.status <= 299,
                data: response.data,
                message: "",
            };
        } catch (ex) {
            const error = ex as AxiosError;
            return {
                success: false,
                data: null as T,
                message: this.extractErrorMessage(error),
            };
        }
    };

    private extractErrorMessage = (error: AxiosError): string => {
        const data = error?.response?.data as Record<string, unknown> | undefined;

        if (!data) {
            return error.message || "An unknown error occurred";
        }

        // Handle { error: "message" } format
        if (typeof data.error === "string") {
            return data.error;
        }

        // Handle { errors: { field: ["message"] } } format
        if (data.errors && typeof data.errors === "object") {
            const errors = data.errors as Record<string, string[]>;
            const messages: string[] = [];
            for (const field in errors) {
                if (Array.isArray(errors[field])) {
                    messages.push(...errors[field]);
                }
            }
            if (messages.length > 0) {
                return messages.join("; ");
            }
        }

        // Handle { title: "message" } format (ASP.NET problem details)
        if (typeof data.title === "string") {
            return data.title;
        }

        // Handle { message: "message" } format
        if (typeof data.message === "string") {
            return data.message;
        }

        return error.message || "An unknown error occurred";
    };
}

const messagingHttpService = new MessagingHttpService();

export { messagingHttpService };
